import type { PropsWithChildren, JSX } from 'react';

import { Col, Row } from 'antd';
import * as _ from 'lodash';
import { Link } from 'react-router-dom';

import { Color, Image } from '../../../styles';
import Card from '../../Miscellaneous/Card';
import { MTypography } from '../../MTypography/MTypography';

import * as Styles from './CardLayout.styles';

interface CardLayoutProps {
  title?: string;
  subtitle?: string | JSX.Element;
  subtitleClassName?: string;
  activeTab?: string;
  tabs?: string[];
  footer?: JSX.Element;
  cardTitle?: string;
  cardSubtitle?: string | JSX.Element;
  cardClassName?: string;
  logoUrl?: string;
  logoPath?: string;
  rightSlot?: JSX.Element;
}

export const CardLayout = ({
  children,
  activeTab,
  title,
  subtitle,
  subtitleClassName = '',
  tabs = [],
  footer,
  cardTitle,
  cardSubtitle,
  cardClassName = '',
  logoPath,
  logoUrl,
  rightSlot,
}: PropsWithChildren<CardLayoutProps>) => {
  const renderTabsIfFound = () => {
    const tabElements = tabs.map((aTab: string) => {
      return (
        <span className={activeTab === aTab ? Styles.activeTab : Styles.tab} key={aTab}>
          {aTab}
        </span>
      );
    });

    if (!_.isEmpty(tabElements)) {
      return <div className={Styles.tabs}> {tabElements}</div>;
    }

    return null;
  };

  const renderTitle = (): JSX.Element | null =>
    title ? (
      <Row justify='center'>
        <MTypography
          variant='h5'
          weight='bold'
          align='center'
          color={Color.Brand6}
          className={Styles.title}
          testId='login-title-testId'>
          {title ?? null}
        </MTypography>
      </Row>
    ) : null;

  const renderSubtitle = (): JSX.Element | null =>
    subtitle ? (
      <div className={Styles.subtitle.concat(` ${subtitleClassName}`).trim()} data-testid='login-subtitle-testId'>
        {subtitle}
      </div>
    ) : null;

  const renderFooterIfFound = (): JSX.Element | null => (footer ? <div className={Styles.footer}>{footer}</div> : null);

  const renderCardTitleIfFound = (): JSX.Element | null =>
    cardTitle ? (
      <MTypography variant='h4' weight='semibold' color={Color.Gray10} className={Styles.cardTitle}>
        {cardTitle}
      </MTypography>
    ) : null;

  const renderCardSubtitleIfFound = (): JSX.Element | null =>
    cardSubtitle ? <div className={Styles.cardSubtitle}>{cardSubtitle}</div> : null;

  return (
    <div>
      <div className={Styles.container}>
        <div className={Styles.main}>
          <div className={Styles.logoContainer}>
            <Link to={logoPath ?? '/'}>
              <img
                src={logoUrl ?? Image.MyIPOLogo}
                alt='My IPO'
                className={Styles.logo}
                data-testid='card-layout-page-img-logo-testId'
              />
            </Link>
          </div>
          {renderTitle()}
          {renderSubtitle()}
          {renderTabsIfFound()}
          <Row className={Styles.cardContainer} justify='center' align='middle'>
            <Card className={Styles.card.concat(` ${cardClassName}`).trim()}>
              <div className={Styles.cardContentContainer}>
                {renderCardTitleIfFound()}
                {renderCardSubtitleIfFound()}
                {children}
              </div>
            </Card>
            <Col className={Styles.rightSlot}>{rightSlot}</Col>
          </Row>
          {renderFooterIfFound()}
        </div>
      </div>
    </div>
  );
};
